import {friendlyObjectRelationDisplay, ObjectModelRelationType} from "../../enums/objectModelRelationType";
import assertNever from "../../../global/assetNever";
import {IConfirmModalConfig} from "~/interfaces/modalInterfaces";
import {IDefaultVOption} from "~/interfaces/vSelectInterfaces";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
import {AccountRelationAssignmentOption} from "../../enums/accountRelationAssignmentOption";

export default class ObjectModelRelationDefinition {
  static getComponent(relationType: ObjectModelRelationType) {
    switch (relationType) {
      case ObjectModelRelationType.OneToOne:
        return 'one-to-one';
      case ObjectModelRelationType.OneToMany:
        return 'one-to-many';
      case ObjectModelRelationType.ManyToOne:
        return 'many-to-one';
      case ObjectModelRelationType.Account:
        return 'account';
      case ObjectModelRelationType.EntityAccount:
        return 'entity-account';
      default:
        assertNever(relationType);
    }
  }

  static getDisplayName(relationType: ObjectModelRelationType) {
    switch (relationType) {
      case ObjectModelRelationType.OneToOne:
        return 'One to One';
      case ObjectModelRelationType.OneToMany:
        return 'One to Many';
      case ObjectModelRelationType.ManyToOne:
        return 'Many to One';
      case ObjectModelRelationType.Account:
        return 'Account';
      case ObjectModelRelationType.EntityAccount:
        return 'Entity Account';
      default:
        assertNever(relationType);
    }
  }

  static getIcon(relationshipType: ObjectModelRelationType) {
    switch (relationshipType) {
      case ObjectModelRelationType.OneToOne:
        return 'icon one-to-one';
      case ObjectModelRelationType.OneToMany:
        return 'icon one-to-many';
      case ObjectModelRelationType.ManyToOne:
        return 'icon many-to-one';
      case ObjectModelRelationType.Account:
        return 'icon account';
      case ObjectModelRelationType.EntityAccount:
        return 'icon members';
      default:
        assertNever(relationshipType);
    }
  }

  static getComponentClass(relationType: ObjectModelRelationType) {
    return `builder-element element-${ObjectModelRelationDefinition.getComponent(relationType)} omr-${ObjectModelRelationDefinition.getComponent(relationType)}`;
  }

  static getControlGroups(): { name: string, icon: string, type: ObjectModelRelationType }[] {
    return [
      {
        name: ObjectModelRelationDefinition.getDisplayName(ObjectModelRelationType.OneToOne),
        icon: ObjectModelRelationDefinition.getIcon(ObjectModelRelationType.OneToOne),
        type: ObjectModelRelationType.OneToOne,
      },
      {
        name: ObjectModelRelationDefinition.getDisplayName(ObjectModelRelationType.OneToMany),
        icon: ObjectModelRelationDefinition.getIcon(ObjectModelRelationType.OneToMany),
        type: ObjectModelRelationType.OneToMany,
      },
      {
        name: ObjectModelRelationDefinition.getDisplayName(ObjectModelRelationType.ManyToOne),
        icon: ObjectModelRelationDefinition.getIcon(ObjectModelRelationType.ManyToOne),
        type: ObjectModelRelationType.ManyToOne,
      },
      {
        name: ObjectModelRelationDefinition.getDisplayName(ObjectModelRelationType.Account),
        icon: ObjectModelRelationDefinition.getIcon(ObjectModelRelationType.Account),
        type: ObjectModelRelationType.Account,
      },
      {
        name: ObjectModelRelationDefinition.getDisplayName(ObjectModelRelationType.EntityAccount),
        icon: ObjectModelRelationDefinition.getIcon(ObjectModelRelationType.EntityAccount),
        type: ObjectModelRelationType.EntityAccount,
      },
    ];
  }

  static getFriendlyName(relationType: ObjectModelRelationType) {
    switch (relationType) {
      case ObjectModelRelationType.OneToOne:
        return 'Has One';
      case ObjectModelRelationType.OneToMany:
        return 'Has Many';
      case ObjectModelRelationType.ManyToOne:
        return 'Has One';
      case ObjectModelRelationType.Account:
        return '';
      case ObjectModelRelationType.EntityAccount:
        return '';
      default:
        assertNever(relationType);
    }
  }

  static getAmountName(relationType: ObjectModelRelationType) {
    switch (relationType) {
      case ObjectModelRelationType.OneToOne:
        return 'one'
      case ObjectModelRelationType.OneToMany:
        return 'many'
      case ObjectModelRelationType.ManyToOne:
        return 'one'
      case ObjectModelRelationType.Account:
        return ''
      case ObjectModelRelationType.EntityAccount:
        return ''
      default:
        assertNever(relationType);
    }
  }

  static inverseRelationshipType(relationType: ObjectModelRelationType): ObjectModelRelationType {
    switch (relationType) {
      case ObjectModelRelationType.OneToOne:
        return ObjectModelRelationType.OneToOne;
      case ObjectModelRelationType.OneToMany:
        return ObjectModelRelationType.ManyToOne;
      case ObjectModelRelationType.ManyToOne:
        return ObjectModelRelationType.OneToMany;
      case ObjectModelRelationType.Account:
        return null;
      case ObjectModelRelationType.EntityAccount:
        return null;
      default:
        assertNever(relationType);
    }
  }

  static changeWarningConfig(): Partial<IConfirmModalConfig> {
    return {
      affirmButtonClass: 'green block',
      affirmText: 'Yes',
      cancelButtonClass: 'red outline block',
      confirmationHeader: 'Are You Sure?',
      confirmationMessage: 'If you publish these changes, data for this relationship will be erased and views could be affected when you publish the model. Are you sure you want to proceed?',
    }
  }

  static mappingValueOption(relation: ObjectModelRelation): IDefaultVOption {
    return {
      label: relation.inverse_label_title,
      value: relation.client_id,
      labelSuffix: `(${friendlyObjectRelationDisplay(relation.relation_type)})`,
    }
  }

  static accountRelationConditionalValueOptions(): IDefaultVOption[] {
    return [
      {label: 'assigned', value: AccountRelationAssignmentOption.assigned},
      {label: 'not assigned', value: AccountRelationAssignmentOption.notAssigned},
    ];
  }
}